"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePhotoUpload } from "@/hooks/use-photo-upload";
import { MAX_PHOTOS } from "@/lib/photos/constants";
import { handleError } from "@/lib/handle-error";
import type { Photo } from "@/lib/products/types";
import type { Id } from "@convex/_generated/dataModel";
import {
  INVALID_PHOTO_FILE_MESSAGE,
  partitionPhotoFiles,
  previewPhotosFromResults,
} from "./condensed-photo-files";

function useBlobPreviewUrls(photos: Photo[]) {
  const previewUrlsRef = useRef(new Set<string>());
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  useEffect(
    () => () => {
      const stillReferenced = new Set(
        photosRef.current.map((photo) => photo.url),
      );
      previewUrlsRef.current.forEach((url) => {
        if (!stillReferenced.has(url)) URL.revokeObjectURL(url);
      });
      previewUrlsRef.current.clear();
    },
    [],
  );
  return useMemo(
    () => ({
      track: (urls: string[]) => {
        urls.forEach((url) => previewUrlsRef.current.add(url));
      },
      release: (urls: string[]) => {
        urls.forEach((url) => URL.revokeObjectURL(url));
        urls.forEach((url) => previewUrlsRef.current.delete(url));
      },
      forget: (url: string) => {
        previewUrlsRef.current.delete(url);
      },
    }),
    [],
  );
}

interface CondensedPhotoFilesOptions {
  photos: Photo[];
  onPhotosChange: (photos: Photo[]) => void;
  generateUploadUrl?: () => Promise<string>;
  attestUpload?: (storageId: Id<"_storage">) => Promise<unknown>;
}

export function useCondensedPhotoFiles({
  photos,
  onPhotosChange,
  generateUploadUrl,
  attestUpload,
}: CondensedPhotoFilesOptions) {
  const [error, setError] = useState<string | null>(null);
  const { upload, isUploading } = usePhotoUpload({
    attestUpload,
    generateUploadUrl,
  });
  const previewUrls = useBlobPreviewUrls(photos);

  const addFiles = useCallback(
    async (fileList: FileList | null) => {
      setError(null);
      const { files, hasInvalidFile } = partitionPhotoFiles(fileList);
      if (hasInvalidFile) setError(INVALID_PHOTO_FILE_MESSAGE);
      const toUpload = files.slice(0, MAX_PHOTOS - photos.length);
      if (toUpload.length === 0) return;

      const previews = toUpload.map((file) => URL.createObjectURL(file));
      previewUrls.track(previews);
      try {
        const results = await upload(toUpload, photos, photos.length);
        onPhotosChange([...photos, ...previewPhotosFromResults(results, previews)]);
      } catch (error) {
        previewUrls.release(previews);
        const handled = handleError(error, { display: "silent" });
        setError(handled.message ?? "We couldn't upload those photos.");
      }
    },
    [upload, photos, onPhotosChange, previewUrls],
  );

  const removePhoto = (photo: Photo) => {
    if (photo.url.startsWith("blob:")) {
      URL.revokeObjectURL(photo.url);
      previewUrls.forget(photo.url);
    }
    onPhotosChange(photos.filter((item) => item.storageId !== photo.storageId));
  };

  return { addFiles, removePhoto, isUploading, error, setError };
}
